/** Matches the `gap-4` the flat list used before stacking. */
export const GAP = 16;
/** How far each buried card pokes above the one in front of it. */
export const PEEK_OFFSET = 10;
export const PEEK_SCALE_STEP = 0.04;
/** Each sliver recedes a little further, so the deck reads as depth rather than as stripes. */
export const PEEK_OPACITY_STEP = 0.12;
/**
 * Slivers behind the two full cards. A single one reads as a stray hairline, so the deck needs
 * enough layers to say "there is more back here" at a glance.
 */
export const MAX_PEEK_LAYERS = 3;
export const TRANSITION_MS = 300;
export const LEAVE_MS = 200;
export const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
/** Soft gradient on whichever edge of the scroll box still has content behind it. */
export const EDGE_FADE = 46;
/** Breathing room inside the scroll box so card shadows are not clipped; also the corner gap. */
export const VIEWPORT_INSET = 24;

export type StackEntry = {
  id: string;
  height: number;
};

export type StackPlacement = {
  id: string;
  offsetY: number;
  scaleX: number;
  height: number;
  opacity: number;
  zIndex: number;
  interactive: boolean;
};

/**
 * `entries` runs newest first. `offsetY` is how far a card's bottom edge sits above the bottom of
 * the canvas, so the newest card is always the one anchored in the corner.
 *
 * Collapsed, the two newest render full size and everything older collapses into slivers behind the
 * second one. Expanded, every card gets its own row.
 */
export const buildStackGeometry = (
  entries: Array<StackEntry>,
  expanded: boolean,
): { placements: Array<StackPlacement>; canvasHeight: number } => {
  const count = entries.length;

  const placements = entries.map((entry, index): StackPlacement => {
    const shared = { id: entry.id, zIndex: count - index };

    if (expanded) {
      const offsetY = entries.slice(0, index).reduce((total, earlier) => total + earlier.height + GAP, 0);

      return { ...shared, offsetY, scaleX: 1, height: entry.height, opacity: 1, interactive: true };
    }

    if (index === 0) {
      return { ...shared, offsetY: 0, scaleX: 1, height: entry.height, opacity: 1, interactive: true };
    }

    if (index === 1) {
      return {
        ...shared,
        offsetY: entries[0].height + GAP,
        scaleX: 1,
        height: entry.height,
        opacity: 1,
        interactive: true,
      };
    }

    // Everything past the stack top clamps to the same few slivers, so 3 buried cards and 30 buried
    // cards occupy identical space.
    const depth = Math.min(index - 1, MAX_PEEK_LAYERS + 1);

    return {
      ...shared,
      offsetY: entries[0].height + GAP + depth * PEEK_OFFSET,
      scaleX: 1 - depth * PEEK_SCALE_STEP,
      // Slivers borrow the stack top's height so they stay uniform even when cards differ in size.
      height: entries[1].height,
      opacity: depth > MAX_PEEK_LAYERS ? 0 : 1 - (depth - 1) * PEEK_OPACITY_STEP,
      interactive: false,
    };
  });

  let canvasHeight = 0;

  if (count === 1) {
    canvasHeight = entries[0].height;
  } else if (count > 1 && expanded) {
    canvasHeight = entries.reduce((total, entry) => total + entry.height, 0) + (count - 1) * GAP;
  } else if (count > 1) {
    canvasHeight = entries[0].height + GAP + entries[1].height + Math.min(count - 2, MAX_PEEK_LAYERS) * PEEK_OFFSET;
  }

  return { placements, canvasHeight };
};
